import React, { useCallback } from 'react';
import { graphql } from 'react-relay';

import AnalyticsListener from '@atlaskit/analytics-next/AnalyticsListener';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';

import { EmbeddedConfluenceSource } from './EmbeddedConfluenceSource.tsx';

const eventTypes = [
	'sendOperationalEvent',
	'sendPageEvent',
	'sendTrackEvent',
	'sendUIEvent',
	'sendScreenEvent',
];

type ConfluenceAnalyticsListenerProps = {
	children: React.ReactNode;
};

// Checks if content attributes should be added to this analytics events
// If so, also retrieves the contentId used to fetch those attributes
// out of the GraphQL cache.
// Currently, the only attributes added are `type` and `subType`
//
// Returns [shouldAddContentAttributes: boolean, contentId: string];
const getContentAttributesInfo = (event: UIAnalyticsEvent) => {
	if (event?.payload?.type === 'sendOperationalEvent') {
		// Exclude operational events
		return [false, ''];
	} else if (event?.payload?.data?.attributes?.contentId) {
		// Use the contentId attribute if it exists
		return [true, event?.payload?.data?.attributes?.contentId];
	} else if (event?.payload?.data?.objectType === 'page') {
		// Otherwise, use the objectId if the object is a page
		return [true, event?.payload?.data?.objectId];
	} else {
		// Otherwise, exclude this event because we don't have a contentId for it
		return [false, ''];
	}
};

export const ConfluenceAnalyticsListener = ({
	children,
}: ConfluenceAnalyticsListenerProps): React.JSX.Element => {
	const readFragmentInterop = useReadFragmentInterop<any>({
		fragmentSpec: graphql`
			fragment analyticsListener_content on Content {
				subType
			}
		`,
	});

	const convertToAnalyticsAttribute = (value: string | undefined | null): boolean | undefined => {
		if (value === null) {
			// A subType of `null` is used for non-live pages
			return false;
		} else if (value === 'live') {
			return true;
		} else {
			// Otherwise, subType was not found or the page had a subType other than 'live'.
			return undefined;
		}
	};

	const sendAnalyticEvents = useCallback(
		(event: UIAnalyticsEvent) => {
			if (!event || !event.payload) {
				if (process.env.NODE_ENV !== 'production') {
					// eslint-disable-next-line no-console
					console.warn(
						'[ConfluenceAnalyticsListener] Analytics event received with no payload. Ignoring.',
					);
				}
				return;
			} else if (!eventTypes.includes(event.payload.type)) {
				if (process.env.NODE_ENV !== 'production') {
					// eslint-disable-next-line no-console
					console.warn(
						`[ConfluenceAnalyticsListener] Received unknown analytics event type: "${event.payload.type}". ` +
							`Expected one of: ${eventTypes.join(', ')}. ` +
							`If you used "eventType" instead of "type", use the Confluence analytics API: ` +
							`createAnalyticsEvent({ type: 'sendTrackEvent', data: { ... } }). Ignoring.`,
					);
				}
				return;
			}

			let contentAttributes = {};
			const [shouldAddContentAttributes, contentId] = getContentAttributesInfo(event);

			if (shouldAddContentAttributes) {
				let data;
				try {
					data = readFragmentInterop({
						id: `Content:${contentId}`,
					});
				} catch {}
				contentAttributes = {
					isLivePage: convertToAnalyticsAttribute(data?.subType || data?.singleContent?.subType),
				};
			}
			void getAnalyticsWebClient().then(
				(analyticWebClientResponse: Record<string, (data: unknown) => void>) => {
					const newData = {
						...event.payload.data,
						attributes: {
							...contentAttributes,
							...event.payload.data?.attributes,
						},
					};
					analyticWebClientResponse[event.payload.type](newData);
				},
			);
		},
		[readFragmentInterop],
	);

	return (
		<AnalyticsListener onEvent={sendAnalyticEvents}>
			<EmbeddedConfluenceSource>{children}</EmbeddedConfluenceSource>
		</AnalyticsListener>
	);
};
